import {action, observable, computed, toJS, runInAction, extendObservable} from 'mobx';
import * as mobx from 'mobx';
import CreateOrEditStore from './CreateOrEditStore';


import {
  listNotificationTriggers,
  getNotificationTrigger,
  updateNotificationTrigger,
  createNotificationTrigger, deleteNotificationTrigger
} from './notificationTriggersApi';
import {recordEvent} from "../../app/plugins/metricPlugin";

const RESULTS_PER_PAGE = 10;

//TODO - Toast Notifications for CUD methods
class NotificationTriggersStore {
  //TODO - maybe bring loading and saving into one var and keep an enum of states
  totalNotificationTriggers = 0;
  currentPage = 0;
  notificationTriggers = [];
  notificationTrigger = undefined;
  loadingNotificationTriggerList = false;
  loadingNotificationTrigger = false;
  hasMore = false;
  errors = undefined;
  savingNotificationTrigger = false;
  saveOrEditStore;
  selected = [];

  constructor() {
    mobx.makeAutoObservable(this, {}, {autoBind: true})
  }

  /**
   * loads a list of notifications
   */
  loadNotificationTriggers = () => {
    this.loadingNotificationTriggerList = true;
    return listNotificationTriggers({limit: RESULTS_PER_PAGE, offset: RESULTS_PER_PAGE * this.currentPage}).then((resp) => {
      runInAction(() => {
        this.hasMore = resp.totalCount > RESULTS_PER_PAGE;
        this.notificationTriggers = resp.notificationTriggers.map(trigger => this.buildNotificationTriggerViewModel(trigger));
        this.totalNotificationTriggers = resp.totalCount;
      })
    }).catch((ex) => runInAction(() => {
      //TODO - what kind of errors?
      this.errors = ex.data;
    })).finally(() => runInAction(() => { this.loadingNotificationTriggerList = false; }));

  }

  buildNotificationTriggerViewModel = (trigger) => {
    switch(trigger.eventType) {
      case EventType.REVIEW_RECEIVED:
        if(trigger.criteria && trigger.criteria.reviewRecommendations){
          trigger.criteria.reviewStars =  [...(trigger.criteria.reviewStars || []), ...trigger.criteria.reviewRecommendations];
        }
        break;
      default:
        // no mutations
    }
    return trigger;
  }

  get totalPages(){
    return Math.ceil(this.totalNotificationTriggers / RESULTS_PER_PAGE);
  }

  /**
     * Navigate to a page
     * @param {number} page the page to navigate
     */
  goToPage(page) {
    this.currentPage = page - 1;
    this.loadNotificationTriggers();
  }

  loadNotificationTrigger(notificationId) {
    this.loadingNotificationTrigger = true;
    getNotificationTrigger(notificationId).then(notificationTrigger => {
      runInAction(() => {
        this.loadingNotificationTrigger = false;
        this.notificationTrigger = notificationTrigger;
      })
    }).catch(ex => {
      this.loadingNotificationTrigger = false;
      //TODO - aside from 404 what else?
      this.errors = ex.data
    }).finally(() => runInAction(() => { this.loadingNotificationTrigger = false; }))
  }

  /**
     * @param {boolean} checked
     * @param {string} notificationTriggerId
     */
  toggleSelection(checked, notificationTriggerId) {
    if(checked){
      this.selected.push(notificationTriggerId)
    }else {
      const index = this.selected.findIndex(id => id === notificationTriggerId)
      if(index !== -1) {
        this.selected.splice(index, 1)
      }
    }
  }

  get selectedLookup() {
    const map = this.selected.reduce((m, current) => {
      m[current] = true
      return m;
    }, {});
    return map
  }

  get hasSelection() {
    return !!this.selected.length
  }

  deleteSelected() {
    recordEvent("NotificationRuleDelete", {
      count: this.selected.length,
      types: this.selected.map(sel => sel.eventType)
    })
    this.loadingNotificationTrigger = true;
    Promise.all(this.selected.map(id => deleteNotificationTrigger(id)))
      .then(() => {
        this.selected = [];
        return this.loadNotificationTriggers();
      })
  }

  // Create or Edits store
  createNewEventTrigger() {
    this.saveOrEditStore = new CreateOrEditStore();
  }

  editEventTrigger(trigger) {
    this.saveOrEditStore = new CreateOrEditStore(toJS(trigger));
  }

  cancelChanges() {
    this.saveOrEditStore = undefined;
  }
  saveChanges() {
    return this.saveOrEditStore.saveNotificationTrigger(this.saveOrEditStore.triggerForApi)
  }
}

export const NotificationChannel = {
  DASHBOARD_NOTIFICATION: "DashboardNotification",
  WEB_BROWSER_NOTIFICATION: "WebBrowserNotification",
  EMAIL: "Email",
  PHONE_CALL: "PhoneCall",
  FACEBOOK_MESSENGER: "FacebookMessenger",
  PUSH_NOTIFICATION: "PushNotification",
  SMS: "Sms"
}

export const EventType = {
  REVIEW_RECEIVED: "ReviewReceived",
  NEW_REVIEW: "NewReview",
  REVIEW_UPDATED: "ReviewUpdated",
  RESPOND_TO_REVIEW: "ReviewRespondedTo",
  GOOGLE_QUESTIONS_AND_ANSWERS: "GoogleQuestionAndAnswer",
  LISTING_INFORMATION_UPDATED: "ListingInformationUpdated",
  LISTING_INFORMATION_NEEDS_REVIEW: "ListingInformationNeedsReview", // If listing mismatches occur
  LISTING_RANK_CHANGED: "ListingRankingChanged",
  WORKFLOW_TASK_CREATED: "WorkflowTaskCreated",  //Prompts users to assign workflow task to a user
  WORKFLOW_TASK_ASSIGNED: "WorkflowTaskAssignedToYou", //Prompts user to assign workflow task
  WORKFLOW_TASK_COMMENTED_ON: "WorkflowTaskCommentedOn", //Allows workflow task manager to monitor workflow task progress
  WORKFLOW_TASK_APPROVAL_NEEDED: "WorkflowTaskApprovalNeeded",
  WORKFLOW_TASK_COMPLETE: "WorkflowTaskCompleted",
  WORKFLOW_TASK_OVERDUE: "WorkflowTaskOverdue",
  SOCIAL_POST: "SocialPost",
  SOCIAL_POST_RESPONDED_TO: "SocialPostRespondedTo",
  CREDENTIAL_EXPIRED: "CredentialExpired"
}
export const EventTypeLabels = {
  ReviewReceived: "Review Received",
  ReviewUpdated: "Review Updated",
  NewReview: "New Review",
  ReviewRespondedTo: "Review Responded To",
  GoogleQuestionAndAnswer: "Google Questions & Answers",
  ListingInformationUpdated: "Update Listing Information",
  ListingInformationNeedsReview: "Review Listing Information",
  ListingRankingChanged: "Listing Rank Changed",
  WorkflowTaskCreated: "Workflow Task Created",
  WorkflowTaskAssignedToYou: "Workflow Task Assigned",
  WorkflowTaskCommentedOn: "Workflow Task Commented On",
  WorkflowTaskCompleted: "Workflow Task Completed",
  WorkflowTaskApprovalNeeded: "Workflow Task Approval Needed",
  WorkflowTaskOverdue: "Workflow Task Overdue",
  SocialPost: "New Social Post",
  SocialPostRespondedTo: "Responded To Social Post",
  CredentialExpired: "Expired Credentials"
}

export const EnabledNotifications = [
  { key: EventType.REVIEW_RECEIVED, value: EventTypeLabels.ReviewReceived },
  { key: EventType.REVIEW_UPDATED, value: EventTypeLabels.ReviewUpdated },
  { key: EventType.WORKFLOW_TASK_APPROVAL_NEEDED, value: EventTypeLabels.WorkflowTaskApprovalNeeded },
  { key: EventType.WORKFLOW_TASK_ASSIGNED, value: EventTypeLabels.WorkflowTaskAssignedToYou },
  { key: EventType.WORKFLOW_TASK_OVERDUE, value: EventTypeLabels.WorkflowTaskOverdue },
  // { key: EventType.GOOGLE_QUESTIONS_AND_ANSWERS, value: EventTypeLabels.GoogleQuestionAndAnswer },
  { key: EventType.CREDENTIAL_EXPIRED, value: EventTypeLabels.CredentialExpired }
]
export const ReviewRatingOptions = [
  { key: 1, value: "★" },
  { key: 2, value: "★★" },
  { key: 3, value: "★★★" },
  { key: 4, value: "★★★★" },
  { key: 5, value: "★★★★★" },
  { key: 'Recommended', value: 'Recommended'},
  { key: 'NotRecommended', value: 'Not Recommended'},
]

export const AssigneesOptions = [
  { key: true, value: "Automatic" },
  { key: false, value:  "Custom" }
]


export default new NotificationTriggersStore();
